import { supabase } from '../supabase';
import type {
  RetailProperty,
  RetailPropertyAssignments,
} from '../utils/retail-properties';

/**
 * Preferencias de retail de una exportadora: qué SKUs sigue y cómo los
 * categoriza. Son compartidas por todo el equipo de la exportadora, así que
 * cada guardado viaja con la versión que el cliente leyó; si otro usuario
 * guardó en el medio, el UPDATE no matchea y devolvemos 'conflict' en vez de
 * pisarle los cambios.
 */

export type RetailPreferences = {
  followed: number[];
  properties: RetailProperty[];
  assignments: RetailPropertyAssignments;
};

export type StoredRetailPreferences = RetailPreferences & {
  /** null cuando la exportadora todavía no tiene fila para esta especie. */
  version: number | null;
};

export const EMPTY_RETAIL_PREFERENCES: StoredRetailPreferences = {
  followed: [],
  properties: [],
  assignments: {},
  version: null,
};

const TABLE = 'exporter_retail_preferences';
const COLUMNS = 'followed_sku_ids, properties, assignments, version';
const UNIQUE_VIOLATION = '23505';

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];

// La fila es jsonb: la normalizamos para que una fila vieja o malformada
// degrade a vacío en vez de romper la vista.
const toProperties = (value: unknown): RetailProperty[] =>
  Array.isArray(value)
    ? value.flatMap((item) => {
        if (!item || typeof item !== 'object') return [];
        const { id, name, options } = item as Record<string, unknown>;
        if (typeof id !== 'string' || typeof name !== 'string') return [];
        return [{ id, name, options: asStringArray(options) }];
      })
    : [];

const toAssignments = (value: unknown): RetailPropertyAssignments => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([skuId, byProperty]) => [
      skuId,
      !byProperty || typeof byProperty !== 'object' || Array.isArray(byProperty)
        ? {}
        : Object.fromEntries(
            Object.entries(byProperty as Record<string, unknown>).map(
              ([propertyId, values]) => [propertyId, asStringArray(values)]
            )
          ),
    ])
  );
};

export const getRetailPreferences = async (
  exporterId: number,
  speciesId: number
): Promise<StoredRetailPreferences> => {
  const { data, error } = await supabase
    .from(TABLE)
    .select(COLUMNS)
    .eq('exporter_id', exporterId)
    .eq('species_id', speciesId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) return EMPTY_RETAIL_PREFERENCES;

  return {
    followed: Array.isArray(data.followed_sku_ids)
      ? data.followed_sku_ids.filter((id: unknown) => typeof id === 'number')
      : [],
    properties: toProperties(data.properties),
    assignments: toAssignments(data.assignments),
    version: data.version,
  };
};

export type SaveRetailPreferencesResult =
  | { status: 'saved'; version: number }
  /** Otro usuario de la exportadora guardó primero; hay que releer. */
  | { status: 'conflict' };

export const saveRetailPreferences = async (
  exporterId: number,
  speciesId: number,
  preferences: RetailPreferences,
  version: number | null
): Promise<SaveRetailPreferencesResult> => {
  const values = {
    followed_sku_ids: preferences.followed,
    properties: preferences.properties,
    assignments: preferences.assignments,
  };

  if (version === null) {
    const { data, error } = await supabase
      .from(TABLE)
      .insert({ exporter_id: exporterId, species_id: speciesId, ...values })
      .select('version')
      .single();

    // Alguien creó la fila entre nuestra lectura y este insert.
    if (error?.code === UNIQUE_VIOLATION) return { status: 'conflict' };
    if (error) throw new Error(error.message);
    return { status: 'saved', version: data.version };
  }

  const { data, error } = await supabase
    .from(TABLE)
    .update({ ...values, version: version + 1 })
    .eq('exporter_id', exporterId)
    .eq('species_id', speciesId)
    .eq('version', version)
    .select('version')
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) return { status: 'conflict' };
  return { status: 'saved', version: data.version };
};
